import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    Add as AddIcon,
    CalendarToday as CalendarTodayIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Group as GroupIcon,
    LocationOn as LocationOnIcon,
    Notes as NotesIcon,
    Info as InfoIcon,
    Schedule as ScheduleIcon
} from "@mui/icons-material";
import {
    createEvent,
    deleteEvent,
    getAffiliateEvents,
    getEventRegistrants,
    addUserToEventByAdmin,
    removeEventWaitlistEntry,
    updateEventRegistrationStatus,
    updateEvent
} from "../api/eventApi";
import { getAffiliateById, searchTrainers } from "../api/affiliateApi";
import { formatCurrency } from "../utils/currency";
import SendMessageModal from "./SendMessageModal";

const emptyForm = {
    title: "",
    shortDescription: "",
    description: "",
    location: "",
    eventStartAt: "",
    eventEndAt: "",
    registrationOpenAt: "",
    registrationCloseAt: "",
    capacity: "",
    price: 0,
    currency: "",
    visibility: "public",
    status: "draft",
    requiresApproval: false,
    allowWaitlist: false,
    allowCreditPayment: true,
    allowMontonioPayment: true,
    imageData: ""
};

const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("et-EE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const toDateTimeLocal = (value) => {
    if (!value) return "";
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseFormToPayload = (form, affiliateId, paymentCurrency) => ({
    affiliateId,
    title: form.title,
    shortDescription: form.shortDescription || null,
    description: form.description || null,
    location: form.location || null,
    eventStartAt: form.eventStartAt,
    eventEndAt: form.eventEndAt,
    registrationOpenAt: form.registrationOpenAt || null,
    registrationCloseAt: form.registrationCloseAt || null,
    capacity: form.capacity === "" ? null : Number(form.capacity),
    price: Number(form.price || 0),
    currency: paymentCurrency || "EUR",
    visibility: form.visibility,
    status: form.status,
    requiresApproval: form.requiresApproval,
    allowWaitlist: form.allowWaitlist,
    allowCreditPayment: form.allowCreditPayment,
    allowMontonioPayment: form.allowMontonioPayment,
    imageUrl: null,
    imageData: form.imageData || null
});

const multilineFieldSx = {
    "& .MuiOutlinedInput-root": {
        height: "auto !important",
        alignItems: "flex-start"
    }
};

const AffiliateEvents = ({ affiliateId, affiliateEmail }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [formOpen, setFormOpen] = useState(false);
    const [editEvent, setEditEvent] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const [registrantsOpen, setRegistrantsOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [registrants, setRegistrants] = useState([]);
    const [waitlist, setWaitlist] = useState([]);

    const [emailOpen, setEmailOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsEvent, setDetailsEvent] = useState(null);
    const [addUserOpen, setAddUserOpen] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [addUserFeedback, setAddUserFeedback] = useState("");
    const [paymentCurrency, setPaymentCurrency] = useState("EUR");
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
        confirmText: "Delete",
        action: null,
        payload: null
    });
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    const sortedEvents = useMemo(
        () => [...events].sort((a, b) => new Date(b.eventStartAt) - new Date(a.eventStartAt)),
        [events]
    );

    const loadEvents = async () => {
        if (!affiliateId) return;
        try {
            setLoading(true);
            setError("");
            const response = await getAffiliateEvents(affiliateId);
            setEvents(response.events || []);
        } catch (loadError) {
            console.error("Failed to load events:", loadError);
            setError(loadError?.response?.data?.message || "Failed to load events");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadEvents();
    }, [affiliateId]);

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const response = await getAffiliateById(affiliateId);
                setPaymentCurrency(response?.paymentCurrency || "EUR");
            } catch (currencyError) {
                console.error("AffiliateEvents load currency error:", currencyError);
                setPaymentCurrency("EUR");
            }
        };
        void loadAffiliateCurrency();
    }, [affiliateId]);

    const openCreateDialog = () => {
        setEditEvent(null);
        setForm({
            ...emptyForm,
            currency: paymentCurrency
        });
        setFormOpen(true);
    };

    const openEditDialog = (event) => {
        setEditEvent(event);
        setForm({
            ...emptyForm,
            title: event.title || "",
            shortDescription: event.shortDescription || "",
            description: event.description || "",
            location: event.location || "",
            eventStartAt: toDateTimeLocal(event.eventStartAt),
            eventEndAt: toDateTimeLocal(event.eventEndAt),
            registrationOpenAt: toDateTimeLocal(event.registrationOpenAt),
            registrationCloseAt: toDateTimeLocal(event.registrationCloseAt),
            capacity: event.capacity ?? "",
            price: event.price ?? 0,
            currency: paymentCurrency,
            visibility: event.visibility || "public",
            status: event.status || "draft",
            requiresApproval: Boolean(event.requiresApproval),
            allowWaitlist: Boolean(event.allowWaitlist),
            allowCreditPayment: event.allowCreditPayment !== false,
            allowMontonioPayment: event.allowMontonioPayment !== false,
            imageData: event.imageData || ""
        });
        setFormOpen(true);
    };

    const handleSave = async () => {
        if (!affiliateId) return;
        if (!form.title || !form.eventStartAt || !form.eventEndAt) {
            setError("Title, event start and event end are required");
            return;
        }
        try {
            setSaving(true);
            setError("");
            const payload = parseFormToPayload(form, affiliateId, paymentCurrency);
            if (editEvent) {
                await updateEvent(editEvent.id, payload);
            } else {
                await createEvent(payload);
            }
            setFormOpen(false);
            await loadEvents();
        } catch (saveError) {
            console.error("Failed to save event:", saveError);
            setError(saveError?.response?.data?.message || "Failed to save event");
        } finally {
            setSaving(false);
        }
    };

    const closeConfirmDialog = () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
    };

    const openConfirmDialog = ({ title, message, confirmText = "Delete", action, payload }) => {
        setConfirmDialog({
            open: true,
            title,
            message,
            confirmText,
            action,
            payload
        });
    };

    const handleConfirmAction = async () => {
        const { action, payload } = confirmDialog;
        closeConfirmDialog();

        if (action === "delete-event") {
            await handleDeleteEvent(payload.event);
        } else if (action === "remove-registration") {
            await handleRegistrationStatusUpdate(payload.registrationId, "cancelled");
        } else if (action === "remove-waitlist") {
            await handleRemoveWaitlistEntry(payload.waitlistEntryId);
        }
    };

    const requestDeleteEvent = (event) => {
        openConfirmDialog({
            title: "Confirm Delete",
            message: `Are you sure you want to delete event "${event.title}"? This action cannot be undone.`,
            confirmText: "Delete",
            action: "delete-event",
            payload: { event }
        });
    };

    const handleDeleteEvent = async (event) => {
        try {
            await deleteEvent(event.id);
            await loadEvents();
        } catch (deleteError) {
            console.error("Failed to delete event:", deleteError);
            setError(deleteError?.response?.data?.message || "Failed to delete event");
        }
    };

    const handleImageFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Only image files are supported");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setForm((prev) => ({
                ...prev,
                imageData: String(reader.result || "")
            }));
        };
        reader.readAsDataURL(file);
    };

    const openRegistrants = async (event) => {
        setSelectedEvent(event);
        setRegistrantsOpen(true);
        try {
            const response = await getEventRegistrants(event.id);
            setRegistrants(response.registrants || []);
            setWaitlist(response.waitlist || []);
        } catch (loadError) {
            setError(loadError?.response?.data?.message || "Failed to load registrants");
        }
    };

    const eventMessageRecipients = registrants
        .map((row) => row.user)
        .filter(Boolean)
        .map((user) => ({
            id: user.id,
            fullName: user.fullName,
            email: user.email
        }));

    useEffect(() => {
        if (!addUserOpen) return;

        const query = userSearchQuery.trim();
        if (query.length < 3) {
            setUserSearchResults([]);
            setUserSearchLoading(false);
            return;
        }

        let cancelled = false;
        setUserSearchResults([]);
        const timeoutId = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const users = await searchTrainers(query);
                if (!cancelled) {
                    setUserSearchResults(users || []);
                    setAddUserFeedback("");
                }
            } catch (error) {
                if (!cancelled) {
                    setUserSearchResults([]);
                    setAddUserFeedback("Failed to search users");
                }
            } finally {
                if (!cancelled) {
                    setUserSearchLoading(false);
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [addUserOpen, userSearchQuery]);

    const openAddUserDialog = () => {
        setAddUserFeedback("");
        setUserSearchQuery("");
        setUserSearchResults([]);
        setSelectedUserId("");
        setSelectedUser(null);
        setUserSearchLoading(false);
        setAddUserOpen(true);
    };

    const closeAddUserDialog = () => {
        setAddUserOpen(false);
        setUserSearchQuery("");
        setUserSearchResults([]);
        setSelectedUserId("");
        setSelectedUser(null);
        setUserSearchLoading(false);
    };

    const handleAddUserToEvent = async () => {
        if (!selectedEvent?.id || !selectedUserId) return;
        try {
            setAddUserFeedback("");
            const result = await addUserToEventByAdmin(selectedEvent.id, Number(selectedUserId));
            if (result.waitlisted) {
                setAddUserFeedback("User added to waitlist.");
            } else {
                setAddUserFeedback("User added to event.");
            }
            const refreshed = await getEventRegistrants(selectedEvent.id);
            setRegistrants(refreshed.registrants || []);
            setWaitlist(refreshed.waitlist || []);
            setSelectedUserId("");
            setSelectedUser(null);
            setUserSearchQuery("");
            setUserSearchResults([]);
            await loadEvents();
        } catch (error) {
            setAddUserFeedback(error?.response?.data?.message || "Failed to add user");
        }
    };

    const requestRemoveRegistration = (row) => {
        openConfirmDialog({
            title: row.status === "pending_approval" ? "Confirm Decline" : "Confirm Delete",
            message: `Are you sure you want to ${row.status === "pending_approval" ? "decline" : "remove"} ${row.user?.fullName || row.user?.email || "this user"} from this event?`,
            confirmText: row.status === "pending_approval" ? "Decline" : "Delete",
            action: "remove-registration",
            payload: { registrationId: row.id }
        });
    };

    const handleRegistrationStatusUpdate = async (registrationId, status) => {
        if (!selectedEvent?.id) return;
        try {
            await updateEventRegistrationStatus(selectedEvent.id, registrationId, status);
            const refreshed = await getEventRegistrants(selectedEvent.id);
            setRegistrants(refreshed.registrants || []);
            setWaitlist(refreshed.waitlist || []);
            await loadEvents();
        } catch (error) {
            setError(error?.response?.data?.message || "Failed to update registration status");
        }
    };

    const requestRemoveWaitlistEntry = (row) => {
        openConfirmDialog({
            title: "Confirm Delete",
            message: `Are you sure you want to remove ${row.user?.fullName || row.user?.email || "this user"} from the waitlist?`,
            confirmText: "Delete",
            action: "remove-waitlist",
            payload: { waitlistEntryId: row.id }
        });
    };

    const handleRemoveWaitlistEntry = async (waitlistEntryId) => {
        if (!selectedEvent?.id) return;
        try {
            await removeEventWaitlistEntry(selectedEvent.id, waitlistEntryId);
            const refreshed = await getEventRegistrants(selectedEvent.id);
            setRegistrants(refreshed.registrants || []);
            setWaitlist(refreshed.waitlist || []);
            await loadEvents();
        } catch (error) {
            setError(error?.response?.data?.message || "Failed to remove waitlist user");
        }
    };

    const openDetailsDialog = (event) => {
        setDetailsEvent(event);
        setDetailsOpen(true);
    };

    return (
        <Box>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Events
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                    Create Event
                </Button>
            </Stack>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {loading ? (
                <Typography>Loading events...</Typography>
            ) : sortedEvents.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography color="text.secondary">No events yet. Create your first event.</Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2.5}>
                    {sortedEvents.map((event) => {
                        const registrations = event._count?.registrations || 0;
                        const capacityLabel = event.capacity ? `${registrations}/${event.capacity}` : `${registrations}`;
                        const eventImage = event.imageData || event.imageUrl;
                        const price = Number(event.price || 0);

                        return (
                            <Grid item xs={12} md={6} xl={4} key={event.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                        borderRadius: 3,
                                        borderColor: "rgba(25, 118, 210, 0.14)",
                                        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 16px 38px rgba(15, 23, 42, 0.13)",
                                            borderColor: "rgba(25, 118, 210, 0.35)"
                                        }
                                    }}
                                >
                                    <Box
                                        onClick={() => openDetailsDialog(event)}
                                        sx={{ cursor: "pointer", flexGrow: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                height: 150,
                                                position: "relative",
                                                background: eventImage
                                                    ? "linear-gradient(135deg, rgba(25,118,210,0.15), rgba(21,101,192,0.12))"
                                                    : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 48%, #90caf9 100%)",
                                                overflow: "hidden"
                                            }}
                                        >
                                            {eventImage ? (
                                                <Box
                                                    component="img"
                                                    src={eventImage}
                                                    alt={event.title}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block"
                                                    }}
                                                />
                                            ) : (
                                                <Stack
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{ height: "100%", color: "primary.main" }}
                                                >
                                                    <CalendarTodayIcon sx={{ fontSize: 42, opacity: 0.9 }} />
                                                </Stack>
                                            )}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ position: "absolute", top: 12, left: 12, right: 12, flexWrap: "wrap", gap: 1 }}
                                            >
                                                <Chip
                                                    label={event.status}
                                                    color={event.status === "published" ? "success" : "default"}
                                                    size="small"
                                                    sx={{ fontWeight: 700, textTransform: "capitalize", bgcolor: event.status === "published" ? undefined : "rgba(255,255,255,0.9)" }}
                                                />
                                                <Chip
                                                    label={event.visibility}
                                                    size="small"
                                                    sx={{ bgcolor: "rgba(255,255,255,0.92)", fontWeight: 600, textTransform: "capitalize" }}
                                                />
                                            </Stack>
                                        </Box>

                                        <CardContent sx={{ pb: 1.5 }}>
                                            <Stack spacing={1.5}>
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                        {event.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            mt: 0.75,
                                                            minHeight: 40,
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden"
                                                        }}
                                                    >
                                                        {event.shortDescription || event.description || "No description added yet."}
                                                    </Typography>
                                                </Box>

                                                <Stack spacing={1}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <ScheduleIcon color="primary" fontSize="small" />
                                                        <Typography variant="body2">
                                                            {formatDateTime(event.eventStartAt)}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <LocationOnIcon color="primary" fontSize="small" />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {event.location || "No location provided"}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>

                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    <Chip
                                                        icon={<GroupIcon />}
                                                        label={`${capacityLabel} registered`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={price > 0 ? formatMoney(price) : "Free"}
                                                        size="small"
                                                        color={price > 0 ? "warning" : "success"}
                                                        variant={price > 0 ? "filled" : "outlined"}
                                                    />
                                                    {event.requiresApproval ? (
                                                        <Chip label="Approval required" size="small" variant="outlined" />
                                                    ) : null}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Box>

                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            mt: "auto",
                                            borderTop: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: "rgba(15, 23, 42, 0.02)"
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                                    Registration opens: {formatDateTime(event.registrationOpenAt)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                                    Registration closes: {formatDateTime(event.registrationCloseAt)}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Edit event">
                                                    <IconButton color="primary" size="small" onClick={() => openEditDialog(event)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Registrants">
                                                    <IconButton color="info" size="small" onClick={() => openRegistrants(event)}>
                                                        <GroupIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete event">
                                                    <IconButton color="error" size="small" onClick={() => requestDeleteEvent(event)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="md" fullScreen={fullScreen}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {detailsEvent?.title || "Event details"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Event overview and management
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {detailsEvent ? (
                        <Stack spacing={2}>
                            {detailsEvent.imageData ? (
                                <Box
                                    component="img"
                                    src={detailsEvent.imageData}
                                    alt={detailsEvent.title}
                                    sx={{
                                        width: "100%",
                                        maxHeight: 420,
                                        objectFit: "contain",
                                        borderRadius: 2,
                                        backgroundColor: "grey.100",
                                        border: "1px solid",
                                        borderColor: "divider"
                                    }}
                                />
                            ) : null}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                                <Chip
                                    label={detailsEvent.status}
                                    size="small"
                                    color={detailsEvent.status === "published" ? "success" : "default"}
                                    sx={{ fontWeight: 700, textTransform: "uppercase" }}
                                />
                                <Chip label={detailsEvent.visibility} size="small" variant="outlined" />
                                <Chip
                                    icon={<GroupIcon sx={{ fontSize: 16 }} />}
                                    label={`${detailsEvent._count?.registrations || 0}/${detailsEvent.capacity || "∞"} registered`}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`Price: ${formatMoney(detailsEvent.price)}`}
                                    size="small"
                                    color={Number(detailsEvent.price || 0) > 0 ? "warning" : "success"}
                                    variant={Number(detailsEvent.price || 0) > 0 ? "filled" : "outlined"}
                                />
                            </Stack>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocationOnIcon color="action" fontSize="small" />
                                            <Typography variant="body2">{detailsEvent.location || "Location not specified"}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CalendarTodayIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {formatDateTime(detailsEvent.eventStartAt)} - {formatDateTime(detailsEvent.eventEndAt)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ScheduleIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                Registration: {formatDateTime(detailsEvent.registrationOpenAt)} - {formatDateTime(detailsEvent.registrationCloseAt)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <InfoIcon color="action" fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Short description
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {detailsEvent.shortDescription || "-"}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <NotesIcon color="action" fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Event info
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                            {detailsEvent.description || "-"}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="text" onClick={() => setDetailsOpen(false)}>Close</Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setDetailsOpen(false);
                            if (detailsEvent) openEditDialog(detailsEvent);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setDetailsOpen(false);
                            if (detailsEvent) openRegistrants(detailsEvent);
                        }}
                    >
                        Registrants
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="md" fullScreen={fullScreen}>
                <DialogTitle>{editEvent ? "Edit Event" : "Create Event"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={8}>
                            <TextField label="Title" fullWidth value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField label="Location" fullWidth value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Short description"
                                fullWidth
                                multiline
                                minRows={3}
                                sx={multilineFieldSx}
                                value={form.shortDescription}
                                onChange={(e) => setForm((p) => ({ ...p, shortDescription: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description / Info"
                                fullWidth
                                multiline
                                minRows={6}
                                sx={multilineFieldSx}
                                value={form.description}
                                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField label="Event start" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={form.eventStartAt} onChange={(e) => setForm((p) => ({ ...p, eventStartAt: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="Event end" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={form.eventEndAt} onChange={(e) => setForm((p) => ({ ...p, eventEndAt: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="Registration open" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={form.registrationOpenAt} onChange={(e) => setForm((p) => ({ ...p, registrationOpenAt: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="Registration close" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={form.registrationCloseAt} onChange={(e) => setForm((p) => ({ ...p, registrationCloseAt: e.target.value }))} />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField label="Capacity" type="number" fullWidth value={form.capacity} onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label={`Price (${paymentCurrency})`} type="number" fullWidth value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Visibility</InputLabel>
                                <Select label="Visibility" value={form.visibility} onChange={(e) => setForm((p) => ({ ...p, visibility: e.target.value }))}>
                                    <MenuItem value="public">Public</MenuItem>
                                    <MenuItem value="members_only">Members only</MenuItem>
                                    <MenuItem value="private">Private</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                                    <MenuItem value="draft">Draft</MenuItem>
                                    <MenuItem value="published">Published</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <Button component="label" variant="outlined">
                                    Upload Image
                                    <input hidden type="file" accept="image/*" onChange={handleImageFileChange} />
                                </Button>
                            </Stack>
                            {form.imageData ? (
                                <Typography variant="caption" color="text.secondary">
                                    Image file selected
                                </Typography>
                            ) : null}
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                                <FormControlLabel control={<Switch checked={form.requiresApproval} onChange={(e) => setForm((p) => ({ ...p, requiresApproval: e.target.checked }))} />} label="Requires approval" />
                                <FormControlLabel control={<Switch checked={form.allowWaitlist} onChange={(e) => setForm((p) => ({ ...p, allowWaitlist: e.target.checked }))} />} label="Allow waitlist" />
                                <FormControlLabel control={<Switch checked={form.allowCreditPayment} onChange={(e) => setForm((p) => ({ ...p, allowCreditPayment: e.target.checked }))} />} label="Allow credit payment" />
                                <FormControlLabel control={<Switch checked={form.allowMontonioPayment} onChange={(e) => setForm((p) => ({ ...p, allowMontonioPayment: e.target.checked }))} />} label="Allow Montonio payment" />
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {editEvent ? "Save changes" : "Create event"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={registrantsOpen} onClose={() => setRegistrantsOpen(false)} fullWidth maxWidth="md" fullScreen={fullScreen}>
                <DialogTitle>
                    Registrants - {selectedEvent?.title}
                </DialogTitle>
                <DialogContent>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Registered: {registrants.length} | Waitlist: {waitlist.length}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Button
                                variant="outlined"
                                onClick={openAddUserDialog}
                            >
                                Add user
                            </Button>
                            <Button
                                startIcon={<EmailIcon />}
                                variant="outlined"
                                onClick={() => setEmailOpen(true)}
                                disabled={eventMessageRecipients.length === 0}
                            >
                                Send bulk email
                            </Button>
                        </Stack>
                    </Stack>

                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Registered users</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {registrants.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.user?.fullName || "-"}</TableCell>
                                    <TableCell>{row.user?.email || "-"}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{row.source}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                                            {row.status === "pending_approval" ? (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => handleRegistrationStatusUpdate(row.id, "registered")}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => requestRemoveRegistration(row)}
                                                    >
                                                        Decline
                                                    </Button>
                                                </>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => requestRemoveRegistration(row)}
                                                    aria-label="Remove user from event"
                                                    sx={{ color: "error.main" }}
                                                >
                                                    <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {registrants.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No registrations yet</TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Waitlist</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Position</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {waitlist.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.position}</TableCell>
                                    <TableCell>{row.user?.fullName || "-"}</TableCell>
                                    <TableCell>{row.user?.email || "-"}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => requestRemoveWaitlistEntry(row)}
                                            aria-label="Remove waitlist user"
                                            sx={{ color: "error.main" }}
                                        >
                                            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {waitlist.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No waitlist users</TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegistrantsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <SendMessageModal
                key={selectedEvent?.id || "event-message"}
                open={emailOpen}
                onClose={() => setEmailOpen(false)}
                affiliate={affiliateId}
                affiliateEmail={affiliateEmail}
                preSelectedUsers={eventMessageRecipients}
                initialSubject={selectedEvent ? `Info: ${selectedEvent.title}` : ""}
            />

            <Dialog open={addUserOpen} onClose={closeAddUserDialog} fullWidth maxWidth="sm" fullScreen={fullScreen}>
                <DialogTitle>Add user to event</DialogTitle>
                <DialogContent sx={{ pt: 2.5 }}>
                    <Autocomplete
                        sx={{ mt: 1.5 }}
                        fullWidth
                        options={userSearchResults}
                        filterOptions={(options) => options}
                        loading={userSearchLoading}
                        forcePopupIcon={false}
                        open={userSearchQuery.trim().length >= 3 && (userSearchLoading || userSearchResults.length > 0)}
                        value={selectedUser}
                        inputValue={userSearchQuery}
                        getOptionLabel={(user) => user ? `${user.fullName || user.email} (${user.email})` : ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onInputChange={(event, newInputValue, reason) => {
                            setUserSearchQuery(newInputValue);
                            if (reason === "input" || reason === "clear") {
                                setSelectedUserId("");
                                setSelectedUser(null);
                            }
                        }}
                        onChange={(event, user) => {
                            setSelectedUserId(user ? String(user.id) : "");
                            setSelectedUser(user || null);
                            if (user) {
                                setUserSearchQuery(`${user.fullName || user.email} (${user.email})`);
                            }
                        }}
                        noOptionsText={userSearchQuery.trim().length < 3 ? "Type at least 3 characters" : "No users found"}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search user by name or email"
                                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                helperText={userSearchLoading ? "Searching..." : "Type at least 3 characters to search"}
                            />
                        )}
                    />

                    {addUserFeedback ? (
                        <Alert sx={{ mt: 2 }} severity="info">{addUserFeedback}</Alert>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddUserDialog}>Close</Button>
                    <Button variant="contained" onClick={handleAddUserToEvent} disabled={!selectedUserId}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDialog.open}
                onClose={closeConfirmDialog}
                aria-labelledby="event-confirm-dialog-title"
                aria-describedby="event-confirm-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: "90vw", sm: "500px" }
                    }
                }}
            >
                <DialogTitle
                    id="event-confirm-dialog-title"
                    sx={{
                        fontWeight: "medium",
                        fontSize: { xs: "1.1rem", sm: "1.25rem" }
                    }}
                >
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="event-confirm-dialog-description"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.875rem" } }}
                    >
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={closeConfirmDialog}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            "&:hover": {
                                transform: "translateY(-1px)"
                            },
                            transition: "all 0.2s"
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                            },
                            transition: "all 0.2s"
                        }}
                    >
                        {confirmDialog.confirmText}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AffiliateEvents;
